import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { createLocalizedPath, getPathWithoutLanguage } from '../../utils/languageUtils'
import {
  SITE_ORIGIN_CANONICAL,
  absoluteAssetUrl,
  buildSeoUrl,
  stripTrackingFromSearch,
} from '../../utils/seoUtils'

const LOGO_PATH = '/logo-isuma.png'

/**
 * @param {object} props
 * @param {string} [props.title]
 * @param {string} [props.description]
 * @param {string} [props.keywords]
 * @param {boolean} [props.noindex]
 * @param {{ name: string, pathSuffix: string }[]} [props.breadcrumbItems] pathSuffix: '' | 'news' | 'news/id' (without leading slash)
 */
function SEO({ title, description, keywords, noindex = false, breadcrumbItems }) {
  const { i18n } = useTranslation()
  const location = useLocation()
  const defaultTitle = 'SUMA International - International School Union of Martial Arts'
  const defaultDescription =
    'International School Union of Martial Arts (ISUMA/SUMA International) - Official website'

  useEffect(() => {
    const origin =
      import.meta.env.DEV && typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : import.meta.env.VITE_SITE_ORIGIN || SITE_ORIGIN_CANONICAL

    document.documentElement.lang = i18n.language

    document.title = title && title !== defaultTitle ? `${title} | ${defaultTitle}` : defaultTitle

    let metaRobots = document.querySelector('meta[name="robots"]')
    if (!metaRobots) {
      metaRobots = document.createElement('meta')
      metaRobots.setAttribute('name', 'robots')
      document.head.appendChild(metaRobots)
    }
    metaRobots.setAttribute('content', noindex ? 'noindex, nofollow' : 'index, follow')

    let metaDescription = document.querySelector('meta[name="description"]')
    if (!metaDescription) {
      metaDescription = document.createElement('meta')
      metaDescription.setAttribute('name', 'description')
      document.head.appendChild(metaDescription)
    }
    metaDescription.setAttribute('content', description || defaultDescription)

    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]')
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta')
        metaKeywords.setAttribute('name', 'keywords')
        document.head.appendChild(metaKeywords)
      }
      metaKeywords.setAttribute('content', keywords)
    }

    const logoAbs = absoluteAssetUrl(origin, LOGO_PATH)
    const canonicalUrl = buildSeoUrl(origin, location.pathname, location.search)

    let linkCanonical = document.querySelector('link[rel="canonical"]')
    if (!linkCanonical) {
      linkCanonical = document.createElement('link')
      linkCanonical.setAttribute('rel', 'canonical')
      document.head.appendChild(linkCanonical)
    }
    linkCanonical.setAttribute('href', canonicalUrl)

    const setMeta = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`)
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }
      el.setAttribute('content', value)
    }

    setMeta('property', 'og:image', logoAbs)
    setMeta('name', 'twitter:image', logoAbs)
    setMeta('property', 'og:title', document.title)
    setMeta('property', 'og:description', description || defaultDescription)
    setMeta('property', 'og:url', canonicalUrl)
    setMeta('property', 'og:type', 'website')
    setMeta('name', 'twitter:card', 'summary_large_image')
    setMeta('name', 'twitter:title', document.title)
    setMeta('name', 'twitter:description', description || defaultDescription)
    setMeta('name', 'twitter:url', canonicalUrl)

    const ogLocale =
      i18n.language === 'ru' ? 'ru_RU' : i18n.language === 'ja' ? 'ja_JP' : 'en_US'
    setMeta('property', 'og:locale', ogLocale)

    document.querySelectorAll('meta[property="og:locale:alternate"]').forEach((n) => n.remove())
    const alternates = [
      { code: 'en_US', lang: 'en' },
      { code: 'ru_RU', lang: 'ru' },
      { code: 'ja_JP', lang: 'ja' },
    ].filter((a) => a.lang !== i18n.language)
    alternates.forEach((a) => {
      const m = document.createElement('meta')
      m.setAttribute('property', 'og:locale:alternate')
      m.setAttribute('content', a.code)
      document.head.appendChild(m)
    })

    const cleanSearch = stripTrackingFromSearch(location.search)
    const currentPath = getPathWithoutLanguage(location.pathname)
    const baseUrl = origin

    const setAlternate = (hreflang, pathForLang) => {
      const href = `${baseUrl}${pathForLang}${cleanSearch}`
      let el = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`)
      if (!el) {
        el = document.createElement('link')
        el.setAttribute('rel', 'alternate')
        el.setAttribute('hreflang', hreflang)
        document.head.appendChild(el)
      }
      el.setAttribute('href', href)
    }

    const pathEn = createLocalizedPath(currentPath, 'en')
    const pathRu = createLocalizedPath(currentPath, 'ru')
    const pathJa = createLocalizedPath(currentPath, 'ja')
    setAlternate('en', pathEn)
    setAlternate('ru', pathRu)
    setAlternate('ja', pathJa)
    setAlternate('x-default', pathEn)

    let existingLd = document.getElementById('seo-breadcrumb-jsonld')
    if (existingLd) existingLd.remove()
    if (breadcrumbItems?.length) {
      const items = breadcrumbItems.map((item, idx) => {
        const routerPath = item.pathSuffix
          ? `/${String(item.pathSuffix).replace(/^\//, '')}`
          : '/'
        const langPath =
          routerPath === '/'
            ? createLocalizedPath('/', i18n.language)
            : createLocalizedPath(routerPath, i18n.language)
        const url = `${origin}${langPath}`
        return {
          '@type': 'ListItem',
          position: idx + 1,
          name: item.name,
          item: url,
        }
      })
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.id = 'seo-breadcrumb-jsonld'
      script.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items,
      })
      document.head.appendChild(script)
    }

    document.documentElement.setAttribute('data-seo-ready', '1')
    return () => {
      document.documentElement.removeAttribute('data-seo-ready')
    }
  }, [
    title,
    description,
    keywords,
    noindex,
    breadcrumbItems,
    defaultTitle,
    defaultDescription,
    i18n.language,
    location.pathname,
    location.search,
  ])

  return null
}

export default SEO
